'use client';

import React, { useState } from 'react';
import { useTranslations } from 'next-intl';
import { useCart } from '../../../../components/CartProvider';
import { Produto, money, moneyMensal } from '../../../../lib/store-api';

/**
 * Renderizacao do produto em si. Recebe `produto` ja resolvido (buscado no
 * servidor, em ProdutoPage) em vez de buscar num useEffect - mesma correcao
 * de soft-404 aplicada em artigos/[slug]/ArtigoConteudo.tsx (ver o
 * comentario la para o diagnostico completo). Continua 'use client' pela
 * galeria de imagens, o stepper de quantidade e o botao de adicionar ao
 * carrinho (useCart, estado do CartProvider) - mas isso nao impede o HTML
 * do produto (nome, preco, descricao) de ser enviado pronto no SSR.
 */
export default function ProdutoConteudo({ produto }: { produto: Produto }) {
  const t = useTranslations('paginaProduto');
  const { addItem } = useCart();

  const [imagemAtiva, setImagemAtiva] = useState(0);
  const [quantidade, setQuantidade] = useState(1);
  const [adicionando, setAdicionando] = useState(false);
  const [feedback, setFeedback] = useState('');
  const [feedbackOk, setFeedbackOk] = useState(false);

  async function handleAdd() {
    if (!produto.disponivel) return;
    setAdicionando(true);
    setFeedback('');
    try {
      await addItem(produto.id, quantidade);
      setFeedback(t('feedbackSucesso'));
      setFeedbackOk(true);
    } catch {
      setFeedback(t('feedbackErro'));
      setFeedbackOk(false);
    } finally {
      setAdicionando(false);
    }
  }

  return (
    <div className="grid md:grid-cols-2 gap-12">
      <div>
        <div
          className="bg-gray-100"
          style={{ aspectRatio: '1/1', borderRadius: '4px', overflow: 'hidden' }}
        >
          {produto.imagens && produto.imagens.length > 0 ? (
            <img
              src={produto.imagens[imagemAtiva]}
              alt={produto.nome}
              className="w-full h-full object-cover"
            />
          ) : (
            <div className="w-full h-full flex items-center justify-center text-gray-300 text-xs">
              {t('semImagem')}
            </div>
          )}
        </div>
        {produto.imagens && produto.imagens.length > 1 && (
          <div className="flex gap-2 mt-3">
            {produto.imagens.map((img, i) => (
              <button
                key={img + i}
                onClick={() => setImagemAtiva(i)}
                className="bg-gray-100"
                style={{
                  width: '56px',
                  height: '56px',
                  borderRadius: '4px',
                  overflow: 'hidden',
                  border: i === imagemAtiva ? '2px solid #000' : '2px solid transparent',
                }}
              >
                <img src={img} alt="" className="w-full h-full object-cover" />
              </button>
            ))}
          </div>
        )}
      </div>

      <div>
        {produto.categoria && (
          <p className="eyebrow mb-2" style={{ color: '#1a9fff' }}>
            {produto.categoria}
          </p>
        )}
        <h1 className="display-section mb-4" style={{ fontSize: 'clamp(32px, 4vw, 52px)' }}>
          {produto.nome}
        </h1>

        {produto.descricaoCurta && (
          <p className="text-gray-600 mb-6" style={{ fontSize: '15px', lineHeight: 1.7 }}>
            {produto.descricaoCurta}
          </p>
        )}

        <div className="mb-6">
          {produto.precoDe && (
            <p className="text-gray-400 text-sm" style={{ textDecoration: 'line-through' }}>
              {money(produto.precoDe)}
            </p>
          )}
          <p className="font-semibold" style={{ fontSize: '32px' }}>
            {moneyMensal(produto.preco)}
          </p>
          {!produto.disponivel && <p className="text-red-600 text-sm mt-1">{t('semEstoque')}</p>}
        </div>

        <div className="flex items-center gap-3 mb-6">
          <div className="flex items-center border border-gray-300 rounded">
            <button
              onClick={() => setQuantidade((q) => Math.max(1, q - 1))}
              className="px-3 py-2 hover:bg-gray-100"
              aria-label={t('diminuirAria')}
            >
              −
            </button>
            <span className="px-4 text-sm" style={{ minWidth: '24px', textAlign: 'center' }}>
              {quantidade}
            </span>
            <button
              onClick={() => setQuantidade((q) => q + 1)}
              className="px-3 py-2 hover:bg-gray-100"
              aria-label={t('aumentarAria')}
            >
              +
            </button>
          </div>

          <button
            onClick={handleAdd}
            disabled={!produto.disponivel || adicionando}
            className="btn-primary"
            style={{ flex: 1, opacity: !produto.disponivel ? 0.4 : 1 }}
          >
            {adicionando ? t('adicionando') : t('adicionarAoCarrinho')}
          </button>
        </div>

        {feedback && (
          <div className="flex items-center flex-wrap gap-4 mb-6">
            <p className="text-sm text-gray-600">{feedback}</p>
            {feedbackOk && (
              <a href="/carrinho" className="btn-primary" style={{ padding: '10px 20px' }}>
                {t('irParaCarrinho')}
              </a>
            )}
          </div>
        )}

        {produto.descricao && (
          <div className="pt-6" style={{ borderTop: '1px solid #f0f0f0' }}>
            <p className="text-sm font-semibold mb-2">{t('detalhes')}</p>
            <p
              className="text-gray-600"
              style={{ fontSize: '14px', lineHeight: 1.75, whiteSpace: 'pre-line' }}
            >
              {produto.descricao}
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
